// ============================================================
// 网站内置中文 → 英文翻译
// 设计规则（避免 519737 号失败模式）：
//  1. 高质量、自然语言的整句/整段翻译，不做"词典式逐词替换"
//  2. 先匹配"整句精确"字典（sentenceMap），未命中时按"短语字典（长优先）"兜底
//  3. 允许专业名词（如 DIN/ISO/A4-70）保留原英文，不做伪翻译
//  4. 翻译失败的字符串不会被贴上"已翻译"标签，保持原文以提醒补全
// ============================================================

export type TargetLang = "en" | "zh";

export const SENTENCE_TRANSLATIONS: Record<string, string> = {
  // --- 首页/产品中心默认回退页面 ---
  "产品中心": "Products",
  "产品库存目录": "Product Inventory Catalog",
  "数据实时同步自库存管理系统，涵盖标准紧固件、定制件和全品类库存物料。":
    "Data is synchronized in real time from the inventory management system and covers standard fasteners, custom parts, and the full range of stocked items.",
  "工程工具": "Engineering Tools",
  "紧固件在线计算器": "Fastener Online Calculators",
  "涵盖公英制单位换算、拧紧扭矩、底孔直径、硬度换算、拉伸剪切强度校核、最小旋合长度、螺栓长度选型、连接面滑移安全系数等 10 个专业计算器。":
    "10 professional calculators covering metric / inch unit conversion, tightening torque, tap drill size, hardness conversion, tension & shear strength verification, minimum thread engagement, bolt length selection, and slip-critical joint safety factor.",
  "可视化页面": "Visual Page",
  "这个页面已经切换到新版前端。": "This page has been migrated to the new frontend.",
  "请进入 Web Builder 添加模块并保存。": "Please open Web Builder to add blocks and save the page.",
  "打开编辑器": "Open Builder",

  // --- 知识库 / 产品目录动态模块默认空态 ---
  "暂无知识文章，请先在后台内容管理中添加。": "No knowledge articles yet. Please add content in the CMS first.",
  "知识文章": "Knowledge Article",
  "没有符合条件的产品，请调整搜索或过滤条件。": "No products match your filters. Please refine your search or category selection.",

  // --- 产品目录元信息格式 ---
  "产品": "Product",
  "标准": "Standard",
  "材料": "Material",
  "表面处理": "Finish",
  "库存": "Stock",
  "暂无图片": "No image available",

  // --- 产品目录工具栏 / 搜索 / 视图 / 分类 ---
  "搜索产品名称、型号、材料、标准…": "Search by product name, model, material, standard…",
  "搜索产品": "Search products",
  "视图切换": "View switcher",
  "网格视图": "Grid view",
  "列表视图": "List view",
  "紧凑视图": "Compact view",
  "按分类分组视图": "Grouped view",
  "网格": "Grid",
  "列表": "List",
  "紧凑": "Compact",
  "按分类": "Grouped",
  "全部": "All",

  // --- 页脚 / 门户栏目 / 工具入口 / 服务支持（这些 SiteLayout 也已有硬编码，这里保留为字典备份）---
  "门户栏目": "Portal",
  "工具入口": "Tools",
  "服务支持": "Support",
  "产品检索": "Product Finder",
  "标准替代": "Cross Reference",
  "图纸询价": "Drawing RFQ",
  "工程评估": "Engineering Review",
  "质量文件": "Quality Documents",
  "采购支持": "Procurement Support",
  "关于我们 / 能力中心 / 行业应用": "About / Capabilities / Industries",
  "产品检索 / 标准替代 / 图纸询价": "Finder / Cross Reference / Drawing RFQ",
  "工程评估 / 质量文件 / 采购支持": "Engineering review / Quality documents / Sourcing desk",
  "工业紧固件解决方案、工程选型支持、标准替代、非标寻源和询价服务。":
    "Industrial fastener solutions, engineering selection support, cross reference, custom sourcing and RFQ services.",

  // --- 导航入口（SiteHeader 右上角）---
  "内容后台": "CMS",
  "编辑本页": "Edit Page",
  "中文": "中文",
  "English": "English",

  // --- 通用 eyerow 与 CTA ---
  "图文说明": "Visual Context",
  "立即询价": "Request a Quote",
  "联系我们": "Contact Us",
  "了解更多": "Learn More",

  "下载资料": "Download Brochure",
  "查看详情": "View Details",

  // --- About / Capabilities 常见模块 ---
  "关于我们": "About Us",
  "公司简介": "Company Profile",
  "企业愿景": "Our Vision",
  "发展历程": "Our Journey",
  "团队规模": "Our Team",
  "资质认证": "Certifications",
  "能力中心": "Capabilities",
  "工程能力": "Engineering Capabilities",
  "质量控制": "Quality Control",
  "供应链管理": "Supply Chain Management",
  "行业应用": "Industries",
  "信任中心": "Trust Center",
  "资料中心": "Resources",
  "知识库": "Knowledge",
  "工程计算器": "Calculator",
  "RFQ询价": "RFQ",
  "首页": "First",

  // --- 通用行业/栏目 H1 / 副标题 ---
  "行业解决方案": "Industrial Solutions",
  "覆盖风电、航空、轨道交通、工程机械、高压输配电等多个高端制造领域。":
    "Serving wind energy, aerospace, rail, heavy machinery, power transmission & distribution and other high-end manufacturing sectors.",
  "资源下载与技术白皮书": "Resources & Technical Whitepapers",
  "汇集产品手册、检验报告、技术白皮书、安装指南等资料。":
    "A library of product catalogs, inspection reports, technical whitepapers and installation guidelines.",
  "可信、可靠、可追溯的供应链保障": "A trustworthy, reliable and traceable supply chain",
  "质量体系、材料追溯、合规声明与信息安全。":
    "Quality systems, material traceability, compliance statements and information security.",
  "提交询价，我们将在 24 小时内回复物料清单与报价。":
    "Submit an RFQ and we will reply with a BOM and quotation within 24 hours.",

  // --- CMS 页面高频整句标题（用户截图："product 展示"）---
  "产品展示": "Product Showcase",
  "产品系列": "Product Lines",
  "产品分类": "Product Categories",
  "核心优势": "Core Advantages",
  "为什么选择我们": "Why Choose Us",
  "客户案例": "Customer Cases",
  "服务流程": "Service Process",
  "企业实力": "Our Strength",
  "资质荣誉": "Certifications & Honors",
  "合作客户": "Our Partners",
  "工厂展示": "Factory Tour",
  "车间实拍": "Workshop Gallery",
  "质量保证": "Quality Assurance",
  "售后服务": "After-Sales Service",
  "新闻中心": "News Center",
  "行业资讯": "Industry News",
  "公司动态": "Company Updates",
  "招贤纳士": "Careers",
  "人才招聘": "Open Positions",
  "联系方式": "Contact Information",
  "公司地址": "Address",
  "邮政编码": "Postal Code",
  "传真": "Fax",
  "客服热线": "Customer Hotline",
  "工作时间": "Business Hours",
  "在线咨询": "Live Chat",
  "意见反馈": "Feedback",
  "下载中心": "Download Center",
  "资料下载": "Downloads",
  "技术文档": "Technical Documents",
  "行业标准": "Industry Standards",
  "安装指南": "Installation Guides",
  "使用说明": "User Manual",
  "常见问题": "FAQ",
  "技术支持": "Technical Support",
  "返回顶部": "Back to Top",
  "阅读更多": "Read More",
  "查看更多": "View More",
  "更多详情": "More Details",
  "查看全部": "View All",
  "立即下载": "Download Now",
  "在线留言": "Leave a Message",
  "我要询价": "Request a Quote",
  "提交询价": "Submit RFQ",
  "询价单": "RFQ Form",
  "产品对比": "Product Compare",
  "加入收藏": "Add to Favorites",
  "分享到": "Share",
  "扫一扫": "Scan QR",
  "关注我们": "Follow Us",
  "订阅": "Subscribe",
  "免费注册": "Free Sign Up",
  "会员登录": "Login",
  "忘记密码": "Forgot Password",
  "版权所有": "Copyright",
  "备案号": "ICP License",
  "展示": "Showcase",

  // --- CMS 常见按钮 / 标题（用静态字典兜底）---
  "提交": "Submit",
  "重置": "Reset",
  "取消": "Cancel",
  "确认": "Confirm",
  "返回": "Back",
  "下一步": "Next",
  "上一页": "Previous",
  "下一页": "Next",
  "末页": "Last",
  "立即咨询": "Talk to Sales",
  "下载图纸": "Download Drawing",
  "获取报价": "Get a Quote",

  // --- 404 / 错误文案（兜底）---
  "页面未找到": "Page not found",
  "您访问的页面不存在或已被移除。": "The page you requested does not exist or has been removed.",
  "返回首页": "Back to Home",

  // --- 知识库文章标题 ---
  "紧固件产品标准对照：GB、ISO、DIN、JIS 如何判断可替换": "Fastener Standard Cross-Reference: How to Determine Interchangeability Across GB, ISO, DIN, and JIS",
  "紧固件表面处理怎么选：防腐、防咬死和功能要求": "How to Choose Fastener Surface Treatment: Corrosion Prevention, Anti-Galling, and Functional Requirements",
  "螺纹基础：规格、螺距、公差与图纸标注": "Thread Basics: Specifications, Pitch, Tolerances, and Drawing Annotations",
  "紧固件材料知识：碳钢、不锈钢、铜合金和工程塑料": "Fastener Materials: Carbon Steel, Stainless Steel, Copper Alloys, and Engineering Plastics",
  "紧固件选型输入清单：询价前必须明确的 9 类信息": "Fastener Selection Input Checklist: 9 Types of Information to Clarify Before RFQ",
  "紧固件六步决策流程：从受力分析到等同标准替换": "Six-Step Fastener Selection Process: From Force Analysis to Equivalent Standard Substitution",
  "松动失效：振动工况下预紧力为什么会衰减": "Loosening Failure: Why Preload Decays Under Vibration",
  "腐蚀失效：异种金属、缝隙腐蚀和镀层破坏": "Corrosion Failure: Dissimilar Metals, Crevice Corrosion, and Coating Damage",
  "机器人紧固件材料：轻量化、强度和耐久性的平衡": "Robot Fastener Materials: Balancing Lightweight, Strength, and Durability",
  "自动化装配紧固件：送钉、拧紧与质量追溯": "Automated Assembly Fasteners: Feeding, Tightening, and Quality Traceability",

  // --- 知识库摘要 ---
  "从手册的标准族对照章节整理，说明跨标准替换时应同时核对尺寸、性能等级、材料、表面处理和图纸版本，避免只按标准号替换。": "Compiled from the standard cross-reference chapter: when substituting across standards, verify dimensions, performance class, material, surface treatment, and drawing version simultaneously—don't rely on standard number alone.",
  "基于手册表面处理章节，整理镀锌、锌镍、热浸锌、达克罗、不锈钢钝化等方案的选型逻辑。": "Based on the surface treatment chapter: selection logic for zinc plating, zinc-nickel, hot-dip galvanizing, Geomet, and stainless steel passivation.",
  "从螺纹基础章节整理，帮助工程和采购快速理解米制螺纹、英制螺纹、管螺纹及公差等级。": "Compiled from the thread basics chapter: helps engineers and procurement quickly understand metric, imperial, and pipe threads plus tolerance classes.",
  "整理手册材料知识章节，建立材料强度、耐腐蚀、温度和加工方式之间的选型关系。": "Compiled from the materials chapter: establishes the relationship between material strength, corrosion resistance, temperature, and processing methods.",
  "来自手册选型流程的输入信息章节，用于把客户需求转化为可报价、可验证的技术条件。": "From the selection input chapter: translates customer requirements into quotable and verifiable technical specifications.",
  "整理手册六步决策流程，适合用于工程团队和采购团队共同评审选型。": "Compiled from the six-step decision process: suitable for joint engineering and procurement review of selection decisions.",
  "基于手册松动失效章节，说明自松动、嵌入松弛和防松方案选择。": "Based on the loosening failure chapter: explains self-loosening, embedment relaxation, and anti-loosening solution selection.",
  "整理手册腐蚀失效内容，帮助识别紧固件腐蚀根因并提出预防措施。": "Compiled from the corrosion failure chapter: helps identify root causes of fastener corrosion and proposes preventive measures.",
  "来自人形机器人章节，整理钛合金、不锈钢、高强钢和工程塑料在机器人连接中的取舍。": "From the humanoid robot chapter: trade-offs between titanium alloy, stainless steel, high-strength steel, and engineering plastics in robot connections.",
  "整理自动化装配章节，说明紧固件如何适配送钉系统、自动拧紧和过程质量控制。": "Compiled from the automated assembly chapter: how fasteners adapt to feeding systems, automatic tightening, and process quality control.",

  // --- 知识库正文段落 ---
  "跨标准替换不能只看名称相似。工程采购应先确认产品类型和尺寸系列，再核对螺纹规格、头型、长度、性能等级、材料和表面处理。GB/T、ISO、DIN、JIS 在很多通用件上存在等同或近似关系，但图纸上必须写清完整标准号和版本。对关键连接件，建议保留原标准、可替代标准、验证条件和客户确认记录，避免后续批次争议。": "Cross-standard substitution cannot rely on name similarity alone. Engineering procurement should first confirm the product type and dimension series, then verify thread specification, head type, length, performance class, material, and surface treatment. GB/T, ISO, DIN, and JIS have equivalent or approximate relationships for many common parts, but drawings must specify the complete standard number and version. For critical connections, retain the original standard, alternative standards, verification conditions, and customer confirmation records to avoid future batch disputes.",
  "表面处理首先服务于使用环境，而不是外观。室内普通环境可考虑电镀锌；户外、盐雾或温湿循环场景应提高到锌镍、达克罗或热浸锌；不锈钢件需要关注钝化、防咬死和异种金属接触。高强度钢件要特别评估氢脆风险，电镀后需要按规范做去氢处理。采购询价时建议同时给出盐雾要求、颜色、摩擦系数、禁限用物质和检测文件要求。": "Surface treatment primarily serves the operating environment, not appearance. Electro-zinc plating may suffice for ordinary indoor environments; outdoor, salt spray, or temperature-humidity cycling scenarios should upgrade to zinc-nickel, Geomet, or hot-dip galvanizing. Stainless steel parts require attention to passivation, anti-galling, and dissimilar metal contact. High-strength steel parts need special hydrogen embrittlement risk assessment, and post-plating dehydrogenation treatment per specifications. RFQs should specify salt spray requirements, color, friction coefficient, restricted substances, and testing documentation.",
  "螺纹是紧固件连接能力的核心结构。选型时需要同时确认公称直径、螺距、旋向、精度等级、旋合长度和螺纹标准。米制普通螺纹通常用 M 加规格表示，粗牙用于通用连接，细牙适合薄壁、调节或抗松要求更高的场景。英制和管螺纹不能直接用外径近似替代，必须核对牙型角、螺距和密封方式。图纸标注应避免只写 M8、M10 这类不完整信息。": "Threads are the core structure of fastener connection capability. Selection must simultaneously confirm nominal diameter, pitch, thread direction, accuracy class, engagement length, and thread standard. Metric coarse threads are designated with M plus specification; coarse pitch is for general connections, while fine pitch suits thin-walled, adjustment, or higher anti-loosening scenarios. Imperial and pipe threads cannot be approximated by outer diameter alone—thread form angle, pitch, and sealing method must be verified. Drawings should avoid incomplete designations like M8 or M10.",
  "材料选择要从载荷、环境、温度、重量和成本一起判断。碳钢适合大多数结构连接，高强度等级可提供更高承载能力；不锈钢适合防腐场景，但要关注咬死和强度等级；铜合金常用于导电、装饰或嵌件；工程塑料适合绝缘、轻载和防腐需求。涉及铝、镁、碳纤维或不同金属组合时，需要评估电化学腐蚀，并通过隔离垫圈、涂层或材料替换降低风险。": "Material selection should consider load, environment, temperature, weight, and cost together. Carbon steel suits most structural connections, with high-strength grades providing greater load capacity; stainless steel suits corrosion scenarios but requires attention to galling and strength class; copper alloys are commonly used for conductive, decorative, or insert applications; engineering plastics suit insulation, light-load, and corrosion-resistant needs. When involving aluminum, magnesium, carbon fiber, or dissimilar metal combinations, evaluate electrochemical corrosion and mitigate risks through isolation washers, coatings, or material substitution.",
  "高质量询价不是只给一张照片。选型前建议明确装配场景、被连接件材料、夹持厚度、温度范围、振动等级、腐蚀环境、预紧力要求、拆卸频次、数量与交期。信息越完整，供应商越容易判断标准件、等同替代或非标定制路径。对关键件，还应补充图纸、公差、质量文件、样品验证和失效风险要求。": "A high-quality RFQ is not just a photo. Before selection, clarify assembly scenario, connected part material, clamping thickness, temperature range, vibration level, corrosion environment, preload requirements, disassembly frequency, quantity, and lead time. The more complete the information, the easier for suppliers to determine standard parts, equivalent substitutes, or custom manufacturing paths. For critical parts, supplement with drawings, tolerances, quality documents, sample validation, and failure risk requirements.",
  "推荐流程是先做受力分析，再选择标准族，随后确定材料和性能等级、表面处理、防松方案，最后进行等同标准对照。这个顺序可以减少只凭经验选型造成的返工。对成本敏感项目，可在满足安全边界后比较标准件、现货替代、国产替代和定制件的总成本；对安全件，则应优先保证验证和质量追溯。": "The recommended process starts with force analysis, then selects the standard family, followed by material and performance class, surface treatment, anti-loosening solution, and finally equivalent standard cross-reference. This order reduces rework caused by experience-based selection alone. For cost-sensitive projects, compare total cost of standard parts, stock alternatives, domestic alternatives, and custom parts after meeting safety boundaries; for safety-critical parts, prioritize validation and quality traceability.",
  "松动失效通常不是螺栓断了，而是连接失去了足够预紧力。横向振动、接触面嵌入、涂层压溃、温度循环和重复拆装都可能导致预紧力下降。防松方案要按风险选：普通场景可用法兰面、锁紧螺母或螺纹锁固剂；高振动场景应考虑楔紧垫圈、机械锁止或扭矩转角控制。装配端还要控制扭矩工具、摩擦系数和复检周期。": "Loosening failure is typically not bolt fracture, but loss of sufficient preload in the connection. Transverse vibration, contact surface embedment, coating crush, temperature cycling, and repeated assembly/disassembly can all cause preload decay. Anti-loosening solutions should be selected by risk: ordinary scenarios can use flange faces, lock nuts, or thread lockers; high-vibration scenarios should consider wedge lock washers, mechanical locking, or torque-angle control. Assembly must also control torque tools, friction coefficient, and re-inspection intervals.",
  "腐蚀失效需要同时看材料组合、环境介质、涂层完整性和结构积液。铝合金与碳钢、不锈钢与碳钢等组合在潮湿或盐雾环境下可能出现电化学腐蚀；垫片下方、螺纹根部和搭接缝容易形成缝隙腐蚀。预防措施包括选择合适镀层、隔离异种金属、优化排水结构、控制划伤，并在图纸和采购规范中明确盐雾和膜厚要求。": "Corrosion failure requires simultaneous examination of material combination, environmental medium, coating integrity, and structural fluid accumulation. Combinations like aluminum alloy with carbon steel, or stainless steel with carbon steel, may cause electrochemical corrosion in humid or salt spray environments; crevice corrosion easily forms under gaskets, at thread roots, and in lap joints. Preventive measures include selecting appropriate coatings, isolating dissimilar metals, optimizing drainage structures, controlling scratches, and specifying salt spray and film thickness requirements in drawings and procurement specifications.",
  "机器人连接件面对的是轻量化、高循环、有限空间和维护便利性的综合约束。钛合金适合轻量高强但成本高；A2/A4 不锈钢适合防腐和外观件；10.9/12.9 高强钢适合高载连接但要控制表面处理风险；黄铜和工程塑料更适合电子区、绝缘或轻载固定。选型时应按部位分级，不要在整机上使用单一材料方案。": "Robot connection components face combined constraints of lightweight, high-cycle life, limited space, and maintainability. Titanium alloy suits lightweight high-strength but is costly; A2/A4 stainless steel suits corrosion-resistant and cosmetic parts; 10.9/12.9 high-strength steel suits high-load connections but requires surface treatment risk control; brass and engineering plastics are better for electronic, insulating, or light-load applications. Selection should be graded by location rather than using a single material approach across the entire machine.",
  "自动化装配要求紧固件不仅能连接，还要能稳定上料和拧紧。头型、槽型、长度公差、表面摩擦、毛刺和混料都会影响送钉稳定性。自动拧紧系统应记录扭矩、角度、结果判定和异常报警，关键工位需要防错和追溯。前期选型时建议让供应商参与样件验证，提前确认包装方式、来料一致性和设备兼容性。": "Automated assembly requires fasteners not just to connect, but to feed and tighten consistently. Head type, slot type, length tolerance, surface friction, burrs, and mixing can all affect feeding stability. Automatic tightening systems should record torque, angle, result judgment, and anomaly alarms; key stations need error-proofing and traceability. Early supplier involvement in sample validation is recommended to confirm packaging, incoming consistency, and equipment compatibility in advance.",
};

// 短语级兜底：长优先排序，命中后替换（仅在整句精确匹配失败时使用）
export const PHRASE_TRANSLATIONS: Array<[string, string]> = [
  // 栏目导航/页脚常见
  ["关于我们", "About"],
  ["能力中心", "Capabilities"],
  ["行业应用", "Industry Applications"],
  ["产品中心", "Products"],
  ["资料中心", "Resources"],
  ["信任中心", "Trust Center"],
  ["知识库", "Knowledge"],
  ["工程计算器", "Calculator"],
  ["首页", "Home"],

  // 紧固件行业术语
  ["标准件", "standard parts"],
  ["标准紧固件", "standard fasteners"],
  ["紧固件", "fasteners"],
  ["定制件", "custom parts"],
  ["非标定制", "custom manufacturing"],
  ["寻源服务", "sourcing services"],
  ["询价比价", "RFQ & quotation"],
  ["工程选型", "engineering selection"],
  ["技术支持", "technical support"],
  ["质量文件", "quality documents"],
  ["材料追溯", "material traceability"],
  ["出厂检验", "outgoing inspection"],
  ["来料检验", "incoming inspection"],
  ["全检", "100% inspection"],
  ["第三方检测", "3rd-party lab testing"],
  ["按库存记录", "per stock record"],
  ["按stock记录", "per stock record"],
  ["按库存", "per stock"],
  ["热浸镀锌", "hot-dip galvanized"],
  ["热镀锌", "hot-dip galvanized"],
  ["开口挡圈", "open-type retaining ring"],
  ["开口卡簧", "open-type circlip"],
  ["个/包", "pcs / pack"],
  ["颗/包", "pcs / pack"],
  ["片/包", "pcs / pack"],
  ["/包", " / pack"],

  // --- CMS 模块大标题（短语级，兼容拼接场景："产品展示"/"product 展示"/"PRODUCT 展示"）---
  ["产品展示", "Product Showcase"],
  ["产品系列", "Product Lines"],
  ["产品分类", "Product Categories"],
  ["核心优势", "Core Advantages"],
  ["为什么选择我们", "Why Choose Us"],
  ["客户案例", "Customer Cases"],
  ["服务流程", "Service Process"],
  ["企业实力", "Our Strength"],
  ["资质荣誉", "Certifications & Honors"],
  ["合作客户", "Our Partners"],
  ["工厂展示", "Factory Tour"],
  ["车间实拍", "Workshop Gallery"],
  ["质量保证", "Quality Assurance"],
  ["售后服务", "After-Sales Service"],
  ["新闻中心", "News Center"],
  ["行业资讯", "Industry News"],
  ["公司动态", "Company Updates"],
  ["招贤纳士", "Careers"],
  ["人才招聘", "Open Positions"],
  ["联系方式", "Contact Information"],
  ["公司地址", "Address"],
  ["邮政编码", "Postal Code"],
  ["客服热线", "Customer Hotline"],
  ["工作时间", "Business Hours"],
  ["在线咨询", "Live Chat"],
  ["意见反馈", "Feedback"],
  ["下载中心", "Download Center"],
  ["资料下载", "Downloads"],
  ["技术文档", "Technical Documents"],
  ["行业标准", "Industry Standards"],
  ["安装指南", "Installation Guides"],
  ["使用说明", "User Manual"],
  ["常见问题", "FAQ"],
  ["返回顶部", "Back to Top"],
  ["阅读更多", "Read More"],
  ["查看更多", "View More"],
  ["更多详情", "More Details"],
  ["查看全部", "View All"],
  ["立即下载", "Download Now"],
  ["在线留言", "Leave a Message"],
  ["我要询价", "Request a Quote"],
  ["提交询价", "Submit RFQ"],
  ["产品对比", "Product Compare"],
  ["加入收藏", "Add to Favorites"],
  ["分享到", "Share"],
  ["扫一扫", "Scan QR"],
  ["关注我们", "Follow Us"],
  ["免费注册", "Free Sign Up"],
  ["会员登录", "Login"],
  ["忘记密码", "Forgot Password"],
  ["版权所有", "Copyright"],
  ["备案号", "ICP License"],
  ["展示", "Showcase"],
  ["分类", "Categories"],
  ["系列", "Lines"],
  ["优势", "Advantages"],
  ["案例", "Cases"],
  ["流程", "Process"],
  ["实力", "Strength"],
  ["荣誉", "Honors"],
  ["合作", "Partners"],
  ["工厂", "Factory"],
  ["车间", "Workshop"],
  ["新闻", "News"],
  ["资讯", "News"],
  ["动态", "Updates"],
  ["招聘", "Jobs"],
  ["招贤", "Careers"],
  ["联系", "Contact"],
  ["地址", "Address"],
  ["邮编", "ZIP Code"],
  ["热线", "Hotline"],
  ["咨询", "Consulting"],
  ["反馈", "Feedback"],
  ["下载", "Download"],
  ["文档", "Documents"],
  ["指南", "Guides"],
  ["说明", "Manual"],
  ["问题", "FAQ"],
  ["支持", "Support"],
  ["顶部", "Top"],
  ["更多", "More"],
  ["详情", "Details"],
  ["全部", "All"],
  ["留言", "Message"],
  ["询价", "RFQ"],
  ["对比", "Compare"],
  ["收藏", "Favorites"],
  ["分享", "Share"],
  ["订阅", "Subscribe"],
  ["注册", "Sign Up"],
  ["登录", "Login"],
  ["密码", "Password"],
  ["版权", "Copyright"],
  ["备案", "ICP"],

  // 知识库分类与标签
  ["标准与选型", "Standards & Selection"],
  ["材料与表面处理", "Materials & Surface Treatment"],
  ["螺纹知识", "Thread Knowledge"],
  ["选型流程", "Selection Process"],
  ["失效分析", "Failure Analysis"],
  ["行业应用", "Industry Applications"],
  ["知识文章", "Knowledge Article"],
  ["标准对照", "Standard Cross-Reference"],
  ["防腐", "Corrosion Prevention"],
  ["盐雾", "Salt Spray"],
  ["氢脆", "Hydrogen Embrittlement"],
  ["锌镍", "Zinc-Nickel"],
  ["螺距", "Pitch"],
  ["公差", "Tolerance"],
  ["图纸识读", "Drawing Reading"],
  ["碳钢", "Carbon Steel"],
  ["不锈钢", "Stainless Steel"],
  ["工程塑料", "Engineering Plastics"],
  ["电化学腐蚀", "Electrochemical Corrosion"],
  ["询价", "RFQ"],
  ["选型输入", "Selection Input"],
  ["工程沟通", "Engineering Communication"],
  ["六步流程", "Six-Step Process"],
  ["受力分析", "Force Analysis"],
  ["防松", "Anti-Loosening"],
  ["标准替代", "Standard Substitution"],
  ["松动失效", "Loosening Failure"],
  ["预紧力", "Preload"],
  ["振动", "Vibration"],
  ["腐蚀", "Corrosion"],
  ["异种金属", "Dissimilar Metals"],
  ["缝隙腐蚀", "Crevice Corrosion"],
  ["机器人", "Robot"],
  ["轻量化", "Lightweight"],
  ["钛合金", "Titanium Alloy"],
  ["高强钢", "High-Strength Steel"],
  ["自动化装配", "Automated Assembly"],
  ["送钉", "Feeding"],
  ["拧紧", "Tightening"],
  ["质量追溯", "Quality Traceability"],
  ["表面处理", "Surface Treatment"],
  ["替代选型", "Alternative Selection"],
  ["材料", "Material"],
  ["螺纹", "Thread"],
  ["失效", "Failure"],
  ["图纸", "Drawing"],

  // 表面处理/材料词汇（保留专业名词原文）
  ["不锈钢", "stainless steel"],
  ["碳钢", "carbon steel"],
  ["合金钢", "alloy steel"],
  ["铝合金", "aluminium alloy"],
  ["钛合金", "titanium alloy"],
  ["铜", "copper"],
  ["镀锌", "zinc plated"],
  ["电镀锌", "electro-zinc plated"],
  ["蓝白锌", "clear zinc plated"],
  ["白锌", "white zinc plated"],
  ["彩锌", "yellow zinc plated"],
  ["黑锌", "black zinc plated"],
  ["达克罗", "Geomet / dacromet"],
  ["热浸镀锌", "hot-dip galvanized"],
  ["机械镀锌", "mechanical galvanized"],
  ["磷化", "phosphated"],
  ["发黑", "black oxide"],
  ["钝化", "passivated"],
  ["镀镍", "nickel plated"],
  ["镀锡", "tin plated"],
  ["镀银", "silver plated"],
  ["镀镉", "cadmium plated"],
  ["特氟龙", "PTFE coated"],
  ["渗锌", "sherardized"],
  ["弹簧钢", "spring steel"],
  ["冷轧钢", "cold rolled steel"],
  ["工具钢", "tool steel"],
  ["结构钢", "structural steel"],
  ["轴承钢", "bearing steel"],

  // 通用形容词/提示
  ["在线", "Online"],
  ["实时", "real-time"],
  ["专业", "professional"],
  ["一站式", "one-stop"],
  ["高质量", "high-quality"],
  ["可靠", "reliable"],
  ["高效", "efficient"],
  ["稳定", "stable"],
  ["欢迎", "Welcome"],
];

// 词级术语（最后兜底；避免过度词级替换造成翻译腔）
export const WORD_FALLBACKS: Record<string, string> = {
  // --- 联系方式（页脚 / 联系区块）---
  电话: "Tel",
  邮箱: "Email",
  总部: "Headquarters",
  总部地址: "Headquarters",
  快捷链接: "Quick Links",
  保留所有权利: "All rights reserved",
  // --- 地名（全球布局）---
  天津: "Tianjin",
  苏州: "Suzhou",
  舟山: "Zhoushan",
  加拿大: "Canada",
  上海: "Shanghai",
  北京: "Beijing",
  螺栓: "bolt",
  螺母: "nut",
  垫圈: "washer",
  平垫圈: "flat washer",
  弹垫圈: "lock washer",
  止动垫圈: "lock washer",
  螺钉: "screw",
  自攻螺钉: "self-tapping screw",
  内六角螺钉: "socket head cap screw",
  螺柱: "stud",
  铆钉: "rivet",
  销轴: "pin",
  锚栓: "anchor bolt",
  膨胀螺栓: "expansion anchor",
  化学锚栓: "chemical anchor",
  螺丝: "screw",
  垫片: "washer",
  挡圈: "retaining ring",
  卡簧: "circlip",
  销: "pin",
  键: "key",
  弹簧: "spring",
  柱销: "dowel pin",
  膨胀: "expansion",
  库存: "stock",
  现货: "in stock",
  定制: "custom",
  标准: "standard",
  材质: "material",
  型号: "model",
  规格: "size",
  价格: "price",
  交期: "lead time",
  起订量: "MOQ",
  产品: "product",
  服务: "service",
  方案: "solution",
  应用: "application",
  行业: "industry",
  公司: "company",
  企业: "enterprise",
  我们: "we",
  客户: "customer",
  品质: "quality",
  创新: "innovation",
  诚信: "integrity",
  共赢: "win-win",
  螺母类: "nuts",
  螺栓类: "bolts",
  垫圈类: "washers",
  螺钉类: "screws",
  螺柱类: "studs",
  挡圈类: "retaining rings",
  铆钉类: "rivets",
  销轴类: "pins & shafts",
  定制类: "custom parts",
  组合件: "assemblies",
  标准件: "standard parts",
  机械性能: "mechanical properties",
  蓝白锌: "clear zinc plated",
  白锌: "white zinc plated",
  彩锌: "yellow zinc plated",
  黑锌: "black zinc plated",
  镀镍: "nickel plated",
  电镀锌: "electro-zinc plated",
  机械镀锌: "mechanical galvanized",
  弹簧钢: "spring steel",
  冷轧钢: "cold rolled steel",
  工具钢: "tool steel",
  结构钢: "structural steel",
  轴承钢: "bearing steel",
  铜: "copper",
  金属件: "metal parts",
  碳钢: "carbon steel",
  不锈钢: "stainless steel",
  合金钢: "alloy steel",
  铝合金: "aluminium alloy",
  钛合金: "titanium alloy",
  热浸镀锌: "hot-dip galvanized",
  磷化: "phosphated",
  发黑: "black oxide",
  钝化: "passivated",
  达克罗: "Geomet / dacromet",
  特氟龙: "PTFE coated",
  渗锌: "sherardized",
  镀锌: "zinc plated",
  热镀锌: "hot-dip galvanized",
  开口挡圈: "open-type retaining ring",
  开口卡簧: "open-type circlip",
  版权: "Copyright",
  备案: "ICP",
  展示: "Showcase",
  分类: "Categories",
  系列: "Lines",
  优势: "Advantages",
  案例: "Cases",
  流程: "Process",
  实力: "Strength",
  荣誉: "Honors",
  合作: "Partners",
  工厂: "Factory",
  车间: "Workshop",
  新闻: "News",
  资讯: "News",
  动态: "Updates",
  招聘: "Jobs",
  招贤: "Careers",
  联系: "Contact",
  地址: "Address",
  邮编: "ZIP Code",
  热线: "Hotline",
  咨询: "Consulting",
  反馈: "Feedback",
  下载: "Download",
  文档: "Documents",
  指南: "Guides",
  说明: "Manual",
  问题: "FAQ",
  支持: "Support",
  顶部: "Top",
  更多: "More",
  详情: "Details",
  全部: "All",
  留言: "Message",
  询价: "RFQ",
  对比: "Compare",
  收藏: "Favorites",
  分享: "Share",
  注册: "Sign Up",
  登录: "Login",
  密码: "Password",
  个: "pcs",
  颗: "pcs",
  片: "pcs",
  包: "pack",
};

// --------- 质量与中文字符判定 ---------
const CJK_RE = /[\u4e00-\u9fff\u3400-\u4dbf]/;
export function containsChinese(text: string): boolean {
  return CJK_RE.test(text);
}

// 仅允许白名单里的英文专业名词保留，其余英文不用动
const PRESERVE_EN_TOKENS = new Set([
  "DIN", "ISO", "GB", "ANSI", "ASME", "ASTM", "JIS", "BS",
  "UNC", "UNF", "UNJC", "UNJF",
  "A2", "A4", "A2-70", "A4-70", "A2-80", "A4-80",
  "SUS304", "SUS316", "SUS316L", "304", "316", "316L",
  "HRC", "HB", "HV", "MPa", "N·m", "kN", "mm", "μm", "kgf",
  "CAD", "PDF", "RFQ", "BOM", "MOQ", "OEM", "ODM",
  "RonnieTech", "CMS",
]);

// --------- 主入口：translateText（zh → en） ---------
export function translateText(src: string, target: TargetLang = "en"): string {
  if (!src) return src;
  if (target !== "en") return src; // 目前只内置 zh→en；中文页面直接返回原文
  if (!containsChinese(src)) return src; // 无中文字符不处理

  const trimmed = src.trim();
  // 1) 整句精确匹配（标点/空白先标准化一下）
  const sentenceKey = normalizeKey(trimmed);
  const exact = SENTENCE_TRANSLATIONS[sentenceKey] ?? SENTENCE_TRANSLATIONS[trimmed];
  if (exact) return exact;

  // 2) 短语级替换：长优先（按拼音排序是不可靠的，按长度降序处理）
  let out = trimmed;
  const phrases = [...PHRASE_TRANSLATIONS].sort((a, b) => b[0].length - a[0].length);
  for (const [zh, en] of phrases) {
    if (out.includes(zh)) out = out.split(zh).join(en);
  }
  out = cleanupTranslatedText(out);
  if (!containsChinese(out)) return out;

  // 3) 词级兜底：对剩下的中文字符串，按 word dictionary 替换
  const words = Object.entries(WORD_FALLBACKS).sort((a, b) => b[0].length - a[0].length);
  for (const [zh, en] of words) {
    if (out.includes(zh)) out = out.split(zh).join(en);
  }
  out = cleanupTranslatedText(out);
  if (!containsChinese(out)) return out;

  // 4) 仍残留中文时，不伪造翻译，直接返回原文（避免 519737 的假"已翻译"模式）
  return out;
}

function cleanupTranslatedText(value: string) {
  return value
    .replaceAll("product检索", "Product Finder")
    .replaceAll("DrawingRFQ", "Drawing RFQ")
    .replaceAll("采购Support", "Procurement Support")
    .replaceAll("quality documents", "Quality Documents")
    .replaceAll("Standard Substitution", "Cross Reference");
}

function normalizeKey(s: string): string {
  return s
    .replace(/\s+/g, "")
    .replace(/[，。！？、；：""''（）《》【】\-—…·]/g, "")
    .replace(/[,.!?;:()\[\]<>""'\-]/g, "");
}

// HTML 级：对一段 innerHTML，只处理文本节点，不破坏标签与属性
// 返回翻译后的 HTML 字符串（通过浏览器 DOMParser 或服务端最小栈解析：这里使用基于正则的轻量文本提取+替换）
export function translateHtmlText(srcHtml: string, target: TargetLang = "en"): string {
  if (!srcHtml || target !== "en" || !containsChinese(srcHtml)) return srcHtml;

  // 栈式走 HTML：遇到 <tag> 直接入 result；文本段做翻译
  let out = "";
  let i = 0;
  const n = srcHtml.length;
  while (i < n) {
    const ch = srcHtml[i];
    if (ch === "<") {
      const end = srcHtml.indexOf(">", i);
      if (end === -1) {
        out += srcHtml.slice(i);
        break;
      }
      out += srcHtml.slice(i, end + 1);
      i = end + 1;
      continue;
    }
    // 文本段：扫到下一个 '<'
    let next = srcHtml.indexOf("<", i);
    if (next === -1) next = n;
    const text = srcHtml.slice(i, next);
    // 纯空白或无中文跳过
    if (!text.trim() || !containsChinese(text)) {
      out += text;
    } else {
      // placeholder 属性放在标签上，不会被我们扫到；普通 text 节点安全翻译
      out += translateText(text, target);
    }
    i = next;
  }
  return out;
}

// 供 DOM 层使用：遍历元素内所有文本节点，翻译并替换（不破坏事件绑定）
export function translateElement(root: Element, target: TargetLang = "en"): void {
  if (target !== "en") return;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const t = node.nodeValue ?? "";
      if (!t.trim()) return NodeFilter.FILTER_REJECT;
      if (!containsChinese(t)) return NodeFilter.FILTER_REJECT;
      // 跳过脚本/样式/不可见输入 value
      const p = node.parentElement;
      if (!p) return NodeFilter.FILTER_REJECT;
      const tag = p.tagName;
      if (tag === "SCRIPT" || tag === "STYLE" || tag === "NOSCRIPT") return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  const nodes: Text[] = [];
  let cur: Node | null = walker.nextNode();
  while (cur) {
    nodes.push(cur as Text);
    cur = walker.nextNode();
  }
  for (const node of nodes) {
    const orig = node.nodeValue ?? "";
    const trans = translateText(orig, target);
    if (trans !== orig) node.nodeValue = trans;
  }

  // 同步翻译占位符、title、alt 这几个可见属性（保留 href/action 等功能性属性）
  const attrTargets = root.querySelectorAll<HTMLElement>(
    "[placeholder], [title], [aria-label], img[alt], input[aria-label]",
  );
  attrTargets.forEach((el) => {
    ["placeholder", "title", "aria-label"].forEach((attr) => {
      const v = el.getAttribute(attr);
      if (v && containsChinese(v)) {
        const nv = translateText(v, target);
        if (nv !== v) el.setAttribute(attr, nv);
      }
    });
    if (el.tagName === "IMG") {
      const alt = (el as HTMLImageElement).alt;
      if (alt && containsChinese(alt)) {
        const nv = translateText(alt, target);
        if (nv !== alt) (el as HTMLImageElement).alt = nv;
      }
    }
  });
}

// 保留词（在 translateText 之外使用，用于识别无需翻译的 token）
export function isPreservedToken(s: string) {
  return PRESERVE_EN_TOKENS.has(s.trim());
}
